import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {t} from 'i18next';
import './EditIndex.scss';
import {ISelectItem} from '../../../../../../shared/types';
import {
  addremoveItems,
  ArrTreeItem,
  buildIdMap,
  EditIndexDetails,
  filterTree,
  IndexService,
  labelsAndCountFromSelection,
  MAX_INDEX_NAME,
  normalizeEmail,
  normalizeNodes,
  RawTreeNode,
  validateEmail,
  validateIndexName,
  validateSavedSearch,
  validateSourceIds,
} from '../../../helpers/aihelper';
import {ArrDrawer} from '../../../../../common/arr-drawer/ArrDrawer';
import {Box, IconButton, InputAdornment, Typography} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ArrButton from '../../../../../common/arr-button/ArrButton';
import ArrTextField from '../../../../../common/arr-text-field/ArrTextField';
import ArrSingleSelectDropdown from '../../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {Close, Search} from '@mui/icons-material';
import {ReactComponent as InfoIcon} from '../../../../../../assets/images/info-icon.svg';
import ArrSelectionChip from '../../../../../common/arr-selection-chip/ArrSelectionChip';
import ArrTreeView from '../../../../../common/arr-tree-view/ArrTreeView';
import ArrModal from '../../../../../common/arr-modal/ArrModal';
import ConfirmModal from '../../confirm-modal/ConfirmModal';
import ArrTooltipWrapper from '../../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import ArrIconButton from '../../../../../common/arr-icon-button/ArrIconButton';
import {
  useGetFoldersList,
  useGetTagsList,
} from '../../../../../../hooks/tagsFoldering/useTagsFoldering';
import {
  useGetIndicesList,
  useGetAIInvestigateIndexSourceDocumentCount,
  useEditInvestigateIndex,
  useGetSavedSearchList,
} from '../../../../../../hooks/ai/investigate/useInvestigate';
import {
  IAIInvestigateIndicesPayload,
  IAIInvestigateIndexSourceTypeCode,
  ISavedSearch,
} from '../../../../../../api/types/aiInvestigate.type';
import {ToastType} from '../../../../../../shared/constants';
import {
  TranscriptSortColumn,
  TranscriptSortDirection,
} from '../../../AiInvestigateUtils';
import {ITagsList} from '../../../../../../api/types/tagFoldering.type';
import {RootState} from '../../../../../../redux/store';
import {useSelector} from 'react-redux';
import ArrLoadingSpinner from '../../../../../common/arr-loading-spinner/ArrLoadingSpinner';

type Props = {
  openPanel: boolean;
  onClose: () => void;
  onEdited?: (updatedIndexName?: string) => void;
  showToast: (message: string, status: ToastType) => void;
  service?: IndexService;
  editDetails: EditIndexDetails | null;
};

enum DropdownItemsType {
  savedSearch = 'SAVEDSEARCH',
  tags = 'TAG',
  folders = 'FOLDER',
}

const EditIndex: React.FC<Props> = ({
  openPanel,
  onClose,
  onEdited,
  showToast,
  service,
  editDetails,
}) => {
  const userSecurityGroupId = useSelector(
    (state: RootState) => state.authenticatedUser.user.securityGroupId
  );

  const [selectedDataset, setSelectedDataset] = useState<ISelectItem | null>(
    null
  );
  const [indexName, setIndexName] = useState<string | null>(
    editDetails?.indexName ?? null
  );
  const [newName, setNewName] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');

  const [treeSelectedIds, setTreeSelectedIds] = useState<string[]>([]);

  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emails, setEmails] = useState<string[]>([]);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [isUnsavedModalOpen, setIsUnsavedModalOpen] = useState<boolean>(false);
  const [isEditClicked, setIsEditClicked] = useState<boolean>(false);
  const [isNameModalOpen, setIsNameModalOpen] = useState<boolean>(false);

  const [touched, setTouched] = useState({
    newName: false,
    dataset: false,
    tree: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [action, setAction] = useState<ISelectItem | null>(null);
  const [showOverlayLoader, setShowOverlayLoader] = useState(false);
  const [docCount, setDocCount] = useState<number>(0);
  const lastDocCountRequestKeyRef = useRef<string>('');

  const dropdownItems: ISelectItem[] = useMemo(
    () => [
      {
        id: DropdownItemsType.savedSearch,
        value: DropdownItemsType.savedSearch,
        label: t('common.savedSearch'),
      },
      {
        id: DropdownItemsType.tags,
        value: DropdownItemsType.tags,
        label: t('common.tags'),
      },
      {
        id: DropdownItemsType.folders,
        value: DropdownItemsType.folders,
        label: t('common.folders'),
      },
    ],
    []
  );

  const {
    data: foldersList = [],
    refetch: refetchFoldersList,
    dataUpdatedAt: foldersListUpdatedAt,
    isFetching: isLoadingFoldersList,
  } = useGetFoldersList(
    `?securityGroupId=${userSecurityGroupId}&toggelValue=0`,
    false
  );

  const {
    data: tagsData,
    refetch: refetchTagsList,
    dataUpdatedAt: tagsListUpdatedAt,
    isFetching: isLoadingTagsList,
  } = useGetTagsList(
    `?securityGroupId=${userSecurityGroupId}&toggelValue=0`,
    false
  );

  const {
    mutate: fetchSavedSearchList,
    data: savedSearchList = [],
    isPending: isLoadingSavedSearchList,
  } = useGetSavedSearchList();

  const isTreeLoading = useMemo(
    () => isLoadingFoldersList || isLoadingTagsList || isLoadingSavedSearchList,
    [isLoadingFoldersList, isLoadingTagsList, isLoadingSavedSearchList]
  );

  useEffect(() => {
    if (selectedDataset?.value === DropdownItemsType.folders) {
      refetchFoldersList();
    } else if (selectedDataset?.value === DropdownItemsType.tags) {
      refetchTagsList();
    } else if (selectedDataset?.value === DropdownItemsType.savedSearch) {
      fetchSavedSearchList(false);
    }
  }, [selectedDataset]);

  const structuredTreeData = useMemo((): RawTreeNode[] => {
    if (
      !foldersList.length &&
      !tagsData?.tagList.length &&
      !savedSearchList.length
    )
      return [];
    const treeItems = [];

    if (selectedDataset?.value === DropdownItemsType.folders) {
      treeItems.push(...foldersList);
    } else if (selectedDataset?.value === DropdownItemsType.tags) {
      treeItems.push(...(tagsData?.tagList || []));
    } else if (selectedDataset?.value === DropdownItemsType.savedSearch) {
      treeItems.push(...savedSearchList);
    }
    const nodeMap = new Map<string, RawTreeNode>();

    treeItems.forEach((item: ITagsList | ISavedSearch) => {
      nodeMap.set(item.id, {
        id: item.id,
        label: item.textWithoutCount || item.text,
        docCount: item.documentCount,
        children: [],
      });
    });

    const roots: RawTreeNode[] = [];
    treeItems.forEach((item: ITagsList | ISavedSearch) => {
      const node = nodeMap.get(item.id);
      if (!node) return;

      const parent = nodeMap.get(item.parent);
      if (!parent) {
        roots.push(node);
      } else {
        parent.children = [...(parent.children || []), node];
      }
    });

    return roots;
  }, [
    foldersList,
    foldersListUpdatedAt,
    tagsData,
    tagsListUpdatedAt,
    savedSearchList,
    selectedDataset,
  ]);

  const normalizedData: ArrTreeItem[] = useMemo(() => {
    if (structuredTreeData.length > 0) {
      return normalizeNodes(structuredTreeData);
    }
    return [];
  }, [structuredTreeData]);

  const indicesPayload: IAIInvestigateIndicesPayload = useMemo(
    () => ({
      sortingColumn: TranscriptSortColumn.CreatedOn,
      direction: TranscriptSortDirection.DESC,
      columnFilter: [
        {
          columnName: 'indexName',
          value: indexName ?? '',
        },
      ],
    }),
    [indexName]
  );

  const {refetch: refetchIndicesList} = useGetIndicesList(indicesPayload, {
    enabled: false,
  });
  const {mutate: saveEditIndex} = useEditInvestigateIndex(
    (data) => {
      setShowOverlayLoader(false);
      setIsConfirmModalOpen(false);
      showToast(
        t('ai.investigate.createEditIndex.edit.successToastMessage'),
        ToastType.Success
      );
      onEdited?.(indexName ?? '');
      onClose();
    },
    (error) => {
      setShowOverlayLoader(false);
      showToast(
        t('ai.investigate.createEditIndex.edit.errorToastMessage'),
        ToastType.Error
      );
    }
  );
  const {
    mutate: fetchSourceDocumentCount,
    isPending: isApiDocumentCountPending,
  } = useGetAIInvestigateIndexSourceDocumentCount(
    (data) => {
      setDocCount(data.documentCount);
    },
    () => {
      setDocCount(0);
    }
  );

  const canUseDropdown = !!action;
  const canUseTree = selectedDataset !== null;

  const indexNameErr = touched.newName ? validateIndexName(newName) : null;
  const docsErr = touched.tree ? validateSourceIds(treeSelectedIds) : null;

  const hasAnyChanges = () => {
    return (
      indexName !== editDetails?.indexName ||
      treeSelectedIds.length !== 0 ||
      emails.length > 0
    );
  };
  const isSourceIdsValid = () => {
    if (!action) return true;
    if (treeSelectedIds.length !== 0) return true;
    return false;
  };

  const isFormValid = () => {
    return !validateIndexName(indexName) && isSourceIdsValid();
  };

  useEffect(() => {
    if (!openPanel) {
      setSelectedDataset(null);
      setSearchQuery('');
      setAppliedQuery('');
      setTreeSelectedIds([]);
      setEmails([]);
      setEmailInput('');
      setEmailError(null);
      setTouched({newName: false, dataset: false, tree: false});
      setSubmitting(false);
    }
  }, [openPanel]);

  const handleAddremove = (item: ISelectItem | null) => {
    setAction(item);
  };

  const handleDatasetChange = (item: ISelectItem | null) => {
    setSelectedDataset(item);
    setTreeSelectedIds([]);
    setSearchQuery('');
    setAppliedQuery('');
    if (!touched.dataset) setTouched((p) => ({...p, dataset: true}));
  };

  const idMap = useMemo(() => buildIdMap(normalizedData), [normalizedData]);

  const handleTreeSelected = useCallback(
    (value: string[] | null) => {
      if (!canUseTree) return;

      const ids = Array.isArray(value) ? value : [];

      const leafOnlyIds = ids.filter((id) => {
        const node = idMap.get(id);
        return !!(node && (!node.children || node.children.length === 0));
      });

      setTreeSelectedIds(leafOnlyIds);

      if (!touched.tree) {
        setTouched((prev) => ({...prev, tree: true}));
      }
    },
    [canUseTree, idMap, touched.tree, setTouched]
  );

  const treeItems: ArrTreeItem[] = useMemo(() => {
    if (!normalizedData.length) return [];
    if (!appliedQuery) return normalizedData;
    return filterTree(normalizedData, appliedQuery);
  }, [normalizedData, appliedQuery]);

  const selectedLabelsDisplay = useMemo(() => {
    if (!normalizedData.length || treeSelectedIds.length === 0) {
      return '--';
    }
    const {labels} = labelsAndCountFromSelection(treeSelectedIds, idMap);
    return labels.length > 0 ? labels.join(', ') : '--';
  }, [normalizedData, treeSelectedIds, idMap]);

  useEffect(() => {
    if (treeSelectedIds.length > 0) {
      const sourceTypeCode =
        selectedDataset?.value as IAIInvestigateIndexSourceTypeCode;
      const requestKey = `${sourceTypeCode}:${treeSelectedIds.join(',')}`;

      if (lastDocCountRequestKeyRef.current === requestKey) return;
      lastDocCountRequestKeyRef.current = requestKey;

      fetchSourceDocumentCount({
        sourceTypeCode,
        sourceIds: treeSelectedIds,
      });
    } else {
      lastDocCountRequestKeyRef.current = '';
      setDocCount(0);
    }
  }, [selectedDataset, treeSelectedIds]);

  const addEmail = () => {
    const err = validateEmail(emailInput);
    if (err) return setEmailError(err);
    const v = normalizeEmail(emailInput);
    setEmails((p) => [...p.filter((e) => e !== v), v]);
    setEmailInput('');
    setEmailError(null);
  };

  const handleSave = async () => {
    setTouched({newName: true, dataset: true, tree: true});
    if (!isFormValid()) return;

    try {
      setShowOverlayLoader(true);
      const getIndices = await refetchIndicesList();
      const existingIndices = getIndices?.data?.indices || [];

      const isDuplicateIndex = existingIndices.some(
        (item) =>
          item.indexName?.toLowerCase().trim() ===
            (indexName ?? '').toLowerCase().trim() &&
          item.indexName?.toLowerCase().trim() !==
            (editDetails?.indexName?.toLowerCase().trim() ?? '')
      );

      if (isDuplicateIndex) {
        showToast(
          t('ai.investigate.createEditIndex.create.duplicateIndexName'),
          ToastType.Error
        );
        setShowOverlayLoader(false);
        return;
      }
      setShowOverlayLoader(false);
      setIsConfirmModalOpen(true);
    } catch {
      setShowOverlayLoader(false);
      showToast(
        t('ai.investigate.createEditIndex.edit.indexNameValidationFailed'),
        ToastType.Error
      );
    }
  };

  const modalConfirmHandler = async () => {
    setShowOverlayLoader(true);
    saveEditIndex({
      indexId: editDetails?.indexId ?? 0,
      payload: {
        indexName: (indexName ?? '').trim(),
        sourceIds: treeSelectedIds,
        emailIdsToNotify: emails,
        sourceTypeCode: selectedDataset?.value as DropdownItemsType,
        action: action?.value as 'ADD' | 'REMOVE',
      },
    });
  };

  const displaySource = selectedLabelsDisplay ? selectedLabelsDisplay : '--';
  const displayDocCount =
    treeSelectedIds.length === 0 ? '--' : docCount.toLocaleString('en-US');
  const isDocCountValid = action ? Boolean(docCount) : true;

  const note =
    action?.value === 'REMOVE'
      ? t('ai.investigate.createEditIndex.edit.removeDocumentNote')
      : t('ai.investigate.createEditIndex.edit.addDocumentNote');

  return (
    <ArrDrawer
      isOpen={openPanel}
      onClose={onClose}
      showButton={false}
      showHeader={false}
      title={t('ai.investigate.createEditIndex.edit.editIndexTitle')}
      size="small"
      side="right"
      data-testid="createindex-drawer"
    >
      <Box className="create-edit-Index">
        {showOverlayLoader && (
          <ArrLoadingSpinner
            show={showOverlayLoader}
            overlay={true}
            isOpen={showOverlayLoader}
          />
        )}
        <Box className="drawer-container">
          <Box className="drawer-header-box">
            <Box className="drawer-row">
              <Typography
                component="h3"
                role="header"
                aria-label={t(
                  'ai.investigate.createEditIndex.edit.editIndexTitle'
                )}
                className="typo-text headline3_size"
                data-testid="edit-index-edit-index-title"
              >
                {t('ai.investigate.createEditIndex.edit.editIndexTitle')}
              </Typography>
              <Box className="drawer-buttons-box">
                <ArrButton
                  aria-label={t('common.cancel')}
                  aria-live="polite"
                  aria-atomic="false"
                  variant="secondary"
                  size="medium"
                  onClick={() =>
                    hasAnyChanges() ? setIsUnsavedModalOpen(true) : onClose()
                  }
                  disabled={false}
                  dataTestId="edit-index-cancel-button"
                  className="cancel-button"
                >
                  {t('common.cancel')}
                </ArrButton>

                <ArrButton
                  aria-label={t(
                    'ai.investigate.createEditIndex.edit.saveChanges'
                  )}
                  aria-live="polite"
                  aria-atomic="false"
                  aria-disabled={
                    !isFormValid() || submitting || !isDocCountValid
                  }
                  variant="primary"
                  size="medium"
                  disabled={
                    !isFormValid() ||
                    submitting ||
                    !hasAnyChanges() ||
                    !isDocCountValid
                  }
                  onClick={handleSave}
                  dataTestId="save-button"
                  className="save-button"
                >
                  {t('ai.investigate.createEditIndex.edit.saveChanges')}
                </ArrButton>
              </Box>
            </Box>
          </Box>

          <Box className="drawer-body">
            <Box className="form-section">
              <Box className="edit-title">
                <Typography
                  component="h3"
                  role="header"
                  aria-label={
                    indexName ??
                    t('ai.investigate.createEditDetails.edit.initialTitle')
                  }
                  className="typo-text subhead3_size index-title-text"
                  data-testid="edit-index-index-name-text"
                >
                  {indexName ??
                    indexName ??
                    t('ai.investigate.createEditDetails.edit.initialTitle')}
                </Typography>
                <ArrIconButton
                  icon={isEditClicked ? <Close /> : <EditOutlinedIcon />}
                  onClick={() => {
                    if (newName && isEditClicked) {
                      setIsNameModalOpen(true);
                      setIsUnsavedModalOpen(true);
                    } else {
                      setIsEditClicked(!isEditClicked);
                      setNewName(null);
                      setTouched((p) => ({...p, newName: false}));
                    }
                  }}
                  ariaLabel="edit-button"
                  shape="default"
                  size="small"
                  className="projects-grid-edit-icon"
                  data-testid="edit-button"
                  testId="edit-index-edit-button-icon-button"
                />
              </Box>
              {isEditClicked && (
                <Box className="inputsave-box">
                  <ArrTextField
                    className="control indexinput-control"
                    id="index-name"
                    dataTestId="edit-index-name"
                    state={indexNameErr ? 'error' : 'enabled'}
                    aria-label={t(
                      'ai.investigate.createEditIndex.edit.inputPlaceHolder'
                    )}
                    placeholder={t(
                      'ai.investigate.createEditIndex.edit.inputPlaceHolder'
                    )}
                    supportText={
                      indexNameErr ??
                      `${t('ai.investigate.createEditIndex.create.inputSupportText')}: ${MAX_INDEX_NAME}`
                    }
                    sizeVariant="large"
                    onChange={(e: any) => {
                      setNewName(e.target.value);
                    }}
                    onBlur={() => setTouched((p) => ({...p, newName: true}))}
                  />
                  <ArrButton
                    variant="text"
                    className={`add-email-button as-link btn-control no-shrink}`}
                    onClick={() => {
                      if (newName && !indexNameErr) {
                        setIsEditClicked(false);
                        setIndexName(newName);
                        setTouched((p) => ({...p, newName: false}));
                      }
                    }}
                    dataTestId="edit-index-save-button"
                    disabled={
                      !newName?.trim() || newName?.trim() === indexName?.trim()
                    }
                  >
                    {t('common.save')}
                  </ArrButton>
                </Box>
              )}
            </Box>
            <Box
              className={isEditClicked ? 'arr-component-disable' : ''}
              {...(isEditClicked ? {inert: true} : {})}
            >
              <Box className="form-section">
                <Box className="email-head">
                  <Typography
                    component="span"
                    aria-label={t(
                      'ai.investigate.createEditIndex.edit.docCount'
                    )}
                    className="typo-text subhead3_size"
                    data-testid="edit-index-doc-count-text"
                  >
                    {t('ai.investigate.createEditIndex.edit.docCount')}
                  </Typography>
                  <Typography
                    component="span"
                    aria-label={t(
                      'ai.investigate.createEditIndex.create.emailOptional'
                    )}
                    className="typo-text body1_size"
                    data-testid="edit-index-email-optional"
                  >
                    {' ' +
                      t('ai.investigate.createEditIndex.create.emailOptional')}
                  </Typography>
                </Box>

                <ArrSingleSelectDropdown
                  className="control dropdown-way"
                  key="dataset-dropdown"
                  dataTestId="asd-edit-index-dropdown"
                  aria-label={t(
                    'ai.investigate.createEditIndex.edit.dropdownlabel'
                  )}
                  dropdownLabel={t(
                    'ai.investigate.createEditIndex.edit.dropdownlabel'
                  )}
                  dropdownItems={addremoveItems}
                  dropdownItemKey="id"
                  dropdownItemDisplay="label"
                  selectedValues={action}
                  onSelectionChange={handleAddremove}
                  disabled={isEditClicked || submitting}
                  sizeVariant="medium"
                />
              </Box>

              <Box className="form-section">
                <Box className="label-row">
                  <Typography
                    component="span"
                    aria-label={t('ai.summarize.createJob.selectDocuments')}
                    className="typo-text subhead3_size"
                    data-testid="edit-index-select-documents"
                  >
                    {t('ai.summarize.createJob.selectDocuments')}
                  </Typography>
                  <ArrTooltipWrapper
                    data-testid="select-document-tooltip-wrapper"
                    trigger={
                      <IconButton
                        type="button"
                        size="small"
                        className="select-documents-info-button"
                        aria-label={t(
                          'ai.investigate.createEditIndex.create.selectDocumentsInfoAria'
                        )}
                        data-testid="info-icon"
                        disabled={isEditClicked}
                      >
                        <InfoIcon
                          className="info-icon"
                          aria-hidden
                          focusable={false}
                        />
                      </IconButton>
                    }
                  >
                    {t('ai.investigate.createEditIndex.create.sourcetooltip')}
                  </ArrTooltipWrapper>
                </Box>

                <ArrSingleSelectDropdown
                  className="control dropdown-way"
                  key="dataset-dropdown"
                  dataTestId="asd-edit-index-dropdown-med"
                  aria-label={t('common.select')}
                  dropdownLabel={t('common.select')}
                  dropdownItems={dropdownItems}
                  dropdownItemKey="id"
                  dropdownItemDisplay="label"
                  selectedValues={selectedDataset}
                  onSelectionChange={handleDatasetChange}
                  disabled={!canUseDropdown || submitting || isEditClicked}
                  sizeVariant="medium"
                />
              </Box>

              <Box className="counts-row counts-stacked">
                <Box className="select-source-box">
                  <Typography
                    component="span"
                    aria-label={t(
                      'ai.investigate.createEditIndex.create.selectedSource'
                    )}
                    className="typo-text label2_size"
                    data-testid="edit-indexselect-source"
                  >
                    <span
                      className="typo-text body3_size selected-source-heading"
                      data-testid="edit-index-selected-source"
                    >
                      {t(
                        'ai.investigate.createEditIndex.create.selectedSource'
                      )}
                      :
                    </span>
                    {displaySource}
                  </Typography>
                </Box>

                <Box className="doc-count-box">
                  <Typography
                    component="span"
                    aria-label={t(
                      'ai.investigate.createEditIndex.create.docCount'
                    )}
                    className="typo-text body3_size doc-count-heading"
                    data-testid="edit-index-doc-count-prefix"
                  >
                    {t('ai.investigate.createEditIndex.create.docCount')}:{' '}
                  </Typography>
                  {!isApiDocumentCountPending && (
                    <Typography
                      component="span"
                      aria-label={displayDocCount}
                      className="typo-text label2_size"
                      data-testid="edit-index-display-count"
                    >
                      {displayDocCount}
                    </Typography>
                  )}
                  {isApiDocumentCountPending && (
                    <Box className="doc-count-spinner">
                      <ArrLoadingSpinner
                        show={isApiDocumentCountPending}
                        size={18}
                      />
                    </Box>
                  )}
                </Box>
                <Box>
                  <Typography
                    component="span"
                    aria-label={note}
                    className="typo-text body3_size note-label"
                    data-testid="edit-index-note"
                  >
                    {note}
                  </Typography>
                </Box>
              </Box>

              <Box className="form-section search-bar-control">
                <ArrTextField
                  className="control search-control"
                  fullWidth
                  sizeVariant="medium"
                  aria-label={t(
                    'ai.investigate.createEditIndex.create.searchPlaceholder'
                  )}
                  dataTestId="edit-index-search-input"
                  state={
                    !canUseDropdown || isEditClicked ? 'inactive' : 'enabled'
                  }
                  placeholder={t(
                    'ai.investigate.createEditIndex.create.searchPlaceholder'
                  )}
                  value={searchQuery}
                  onChange={(e: any) => setSearchQuery(e.target.value)}
                  onKeyDown={(e: any) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (canUseTree && !isEditClicked) {
                        setAppliedQuery(searchQuery.trim());
                      }
                    }
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconButton
                            aria-label="Search"
                            size="small"
                            onClick={() =>
                              canUseTree &&
                              !isEditClicked &&
                              setAppliedQuery(searchQuery.trim())
                            }
                            className="search-icon-btn"
                            disabled={!canUseDropdown || isEditClicked}
                            data-testid="search-button"
                          >
                            <Search
                              aria-hidden="true"
                              data-testid="search-icon"
                              className={`${canUseDropdown && !isEditClicked ? 'arr-textfield-icon-color' : 'icon-disabled'}`}
                              fontSize="small"
                            />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Box>

              <Box
                className={`tree-container ${!canUseDropdown ? 'is-disabled' : ''}`}
                {...(!canUseDropdown || submitting || isEditClicked
                  ? {inert: true}
                  : {})}
              >
                {!canUseTree && (
                  <div
                    className="typo-text body3_size empty"
                    data-testid="edit-index-div"
                  ></div>
                )}

                {canUseTree && isTreeLoading && (
                  <ArrLoadingSpinner show={isTreeLoading} />
                )}

                {canUseTree && !isTreeLoading && treeItems.length === 0 && (
                  <div
                    className="empty"
                    data-testid="edit-index-no-search-results"
                  >
                    {t('ai.investigate.createEditIndex.create.noSearchResults')}
                  </div>
                )}

                {canUseTree && !isTreeLoading && treeItems.length > 0 && (
                  <Box>
                    <ArrTreeView
                      key={`tree-${selectedDataset?.value}-${appliedQuery}`}
                      setSelectedItem={handleTreeSelected}
                      itemsList={treeItems as unknown as any[]}
                      treeSelectedItems={treeSelectedIds}
                      allowGlobalSelect={true}
                      multipleSelection={true}
                      isShowCountLabel={false}
                      isShowCheckbox={true}
                      isChildrenIndendation={true}
                      data-testid="source-tree-structure"
                    />
                  </Box>
                )}

                {touched.tree && docsErr && (
                  <div
                    className="field-error"
                    data-testid="edit-index-div-doc-error"
                  >
                    {docsErr}
                  </div>
                )}
              </Box>
              <Box className="email-block">
                <Box className="email-head">
                  <Typography
                    component="span"
                    aria-label={t(
                      'ai.investigate.createEditIndex.create.emailHeader'
                    )}
                    className="typo-text subhead3_size"
                    data-testid="edit-index-email-header"
                  >
                    {t('ai.investigate.createEditIndex.create.emailHeader')}
                  </Typography>
                  <Typography
                    component="span"
                    aria-label={t(
                      'ai.investigate.createEditIndex.create.emailOptional'
                    )}
                    className="typo-text body1_size"
                    data-testid="edit-index-optional"
                  >
                    {' ' +
                      t('ai.investigate.createEditIndex.create.emailOptional')}
                  </Typography>
                </Box>
                <Box className="email-subhead">
                  <Typography
                    component="span"
                    aria-label={t(
                      'ai.investigate.createEditIndex.create.emailSubHeader'
                    )}
                    className="typo-text body2_size"
                    data-testid="edit-index-email-sub-header-text"
                  >
                    {t('ai.investigate.createEditIndex.create.emailSubHeader')}
                  </Typography>
                </Box>
                <div className="email-row" data-testid="edit-index-fill-email">
                  <ArrTextField
                    className="control flex-1"
                    sizeVariant="large"
                    fullWidth
                    dataTestId="edit-index-email"
                    aria-label={t(
                      'ai.investigate.createEditIndex.create.emailAddress'
                    )}
                    placeholder={t(
                      'ai.investigate.createEditIndex.create.emailAddress'
                    )}
                    value={emailInput}
                    state={
                      isEditClicked
                        ? 'inactive'
                        : emailError
                          ? 'error'
                          : 'enabled'
                    }
                    onChange={(e: any) => setEmailInput(e.target.value)}
                    onKeyDown={(e: any) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addEmail();
                      }
                    }}
                    supportText={emailError || ''}
                  />

                  <ArrButton
                    variant="text"
                    className={`add-email-button as-link btn-control no-shrink}`}
                    onClick={addEmail}
                    disabled={isEditClicked}
                    dataTestId="edit-index-add-button"
                  >
                    {t('common.add')}
                  </ArrButton>
                </div>

                {emails.length > 0 && (
                  <div
                    className="email-chips"
                    data-testid="edit-index-div-for-email-chips"
                  >
                    {emails.map((em, index) => (
                      <ArrSelectionChip
                        key={em}
                        filterData={{id: em, label: em, isSelected: false}}
                        onClick={() =>
                          setEmails((curr) => curr.filter((x) => x !== em))
                        }
                        showCloseIcon={true}
                        isFilterInactive={isEditClicked}
                        dataTestId={`edit-index-email-chips-${index}`}
                      />
                    ))}
                  </div>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        {isConfirmModalOpen && (
          <ConfirmModal
            isOpen={isConfirmModalOpen}
            onClose={() => {
              setIsConfirmModalOpen(false);
            }}
            selectedDocumentCount={displayDocCount}
            onConfirm={modalConfirmHandler}
          />
        )}
        {isUnsavedModalOpen && (
          <ArrModal
            showModal={isUnsavedModalOpen}
            showClose={false}
            onClose={() => {
              setIsUnsavedModalOpen(false);
            }}
            title={t('common.unsavedTitle')}
            ariaLabel={t('common.unsavedTitle')}
            ariaDescription={t(
              'ai.investigate.createEditIndex.modal.modificationsDescription'
            )}
            description={t(
              'ai.investigate.createEditIndex.modal.modificationsDescription'
            )}
            dataTestId="edit-index-modal"
            buttons={[
              {
                id: '1',
                label: t('common.cancel'),
                onClick: () => {
                  setIsUnsavedModalOpen(false);
                },
                variant: 'secondary',
                ariaLabel: `${t('common.cancel')}. ${t(
                  'ai.investigate.createEditIndex.modal.cancelUnsavedHint'
                )}`,
                dataTestId: 'ei-cancel-btn',
              },
              {
                id: '2',
                label: t('ai.summarize.createJob.confirmModal.confirm'),
                onClick: () => {
                  setIsUnsavedModalOpen(false);
                  isNameModalOpen
                    ? setIsEditClicked(false)
                    : setTimeout(() => {
                        onClose();
                      }, 100);
                },
                variant: 'primary',
                ariaLabel: `${t('ai.summarize.createJob.confirmModal.confirm')}. ${t(
                  'ai.investigate.createEditIndex.modal.saveUnsavedHint'
                )}`,
                dataTestId: 'ei-confirm-btn',
              },
            ]}
          />
        )}
      </Box>
    </ArrDrawer>
  );
};

export default EditIndex;
